import { useEffect, useRef, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import PortalRootEntry from './components/PortalRootEntry'
import SignIn from './components/SignIn'
import Register from './components/Register'
import ConfirmDetails from './components/ConfirmDetails'
import PolicyLinkOtpVerification from './components/PolicyLinkOtpVerification'
import MfaMobileRequired from './components/MfaMobileRequired'
import MfaEnrollmentPrompt from './components/MfaEnrollmentPrompt'
import CheckYourDetails from './components/CheckYourDetails'
import MatchFound from './components/MatchFound'
import Dashboard from './components/Dashboard'
import PolicyView from './components/PolicyView'
import ClaimsLanding from './components/ClaimsLanding'
import RequireClaimsNotBlocked from './components/RequireClaimsNotBlocked'
import PreClaim from './components/PreClaim'
import ClaimTowingQuestion from './components/ClaimTowingQuestion'
import ClaimWindscreenGstStep from './components/ClaimWindscreenGstStep'
import ClaimWindscreenStep from './components/ClaimWindscreenStep'
import ClaimWindscreenDetailStep from './components/ClaimWindscreenDetailStep'
import ClaimWindscreenRepairOptionStep from './components/ClaimWindscreenRepairOptionStep'
import ClaimWindscreenReviewStep from './components/ClaimWindscreenReviewStep'
import ClaimWindscreenSubmittedStep from './components/ClaimWindscreenSubmittedStep'
import ClaimDetailsPlaceholder from './components/ClaimDetailsPlaceholder'
import ClaimCallAssistanceStep from './components/ClaimCallAssistanceStep'
import ClaimTypeSelection from './components/ClaimTypeSelection'
import ClaimOtherGstStep from './components/ClaimOtherGstStep'
import ClaimOtherMultiVehicleDriverStep from './components/ClaimOtherMultiVehicleDriverStep'
import ClaimOtherSingleVehicleCollisionWithStep from './components/ClaimOtherSingleVehicleCollisionWithStep'
import ClaimOtherSingleVehicleDescribeStep from './components/ClaimOtherSingleVehicleDescribeStep'
import ClaimOtherSingleVehicleReviewStep from './components/ClaimOtherSingleVehicleReviewStep'
import ClaimOtherSingleVehicleSubmittedStep from './components/ClaimOtherSingleVehicleSubmittedStep'
import ClaimOtherMultiVehicleOtherPartyStep from './components/ClaimOtherMultiVehicleOtherPartyStep'
import ClaimOtherMultiVehicleDescribeStep from './components/ClaimOtherMultiVehicleDescribeStep'
import ClaimOtherMultiVehicleReviewStep from './components/ClaimOtherMultiVehicleReviewStep'
import ClaimOtherMultiVehicleSubmittedStep from './components/ClaimOtherMultiVehicleSubmittedStep'
import ClaimOtherWeatherDescribeStep from './components/ClaimOtherWeatherDescribeStep'
import ClaimOtherWeatherReviewStep from './components/ClaimOtherWeatherReviewStep'
import ClaimOtherWeatherSubmittedStep from './components/ClaimOtherWeatherSubmittedStep'
import ClaimOtherFireDescribeStep from './components/ClaimOtherFireDescribeStep'
import ClaimOtherFireReviewStep from './components/ClaimOtherFireReviewStep'
import ClaimOtherFireSubmittedStep from './components/ClaimOtherFireSubmittedStep'
import ClaimOtherMaliciousDamageDescribeStep from './components/ClaimOtherMaliciousDamageDescribeStep'
import ClaimOtherMaliciousDamageReviewStep from './components/ClaimOtherMaliciousDamageReviewStep'
import ClaimOtherMaliciousDamageSubmittedStep from './components/ClaimOtherMaliciousDamageSubmittedStep'
import ClaimOtherOtherLossDescribeStep from './components/ClaimOtherOtherLossDescribeStep'
import ClaimOtherOtherLossReviewStep from './components/ClaimOtherOtherLossReviewStep'
import ClaimOtherOtherLossSubmittedStep from './components/ClaimOtherOtherLossSubmittedStep'
import ClaimOtherTheftDescribeStep from './components/ClaimOtherTheftDescribeStep'
import ClaimOtherTheftReviewStep from './components/ClaimOtherTheftReviewStep'
import ClaimOtherTheftSubmittedStep from './components/ClaimOtherTheftSubmittedStep'
import ClaimOtherSubtypeStep from './components/ClaimOtherSubtypeStep'
import ClaimOtherSingleVehicleDriverStep from './components/ClaimOtherSingleVehicleDriverStep'
import ClaimIncidentStep from './components/ClaimIncidentStep'
import PaymentView from './components/PaymentView'
import PolicyDocumentsView from './components/PolicyDocumentsView'
import ChangePaymentMethodView from './components/ChangePaymentMethodView'
import UpdatePolicyAllFields from './components/UpdatePolicyAllFields'
import PolicyUpdateConfirmation from './components/PolicyUpdateConfirmation'
import PortalFAQ from './components/PortalFAQ'
import TestEntsia from './components/TestEntsia'
import TestGooglePlaces from './components/TestGooglePlaces'
import AuthProvider from './components/AuthProvider'
import InactivityGuard from './components/InactivityGuard'
import SkipLink from './components/SkipLink'
import DocumentTitle from './components/DocumentTitle'
import GtmPageView from './components/GtmPageView'
import { shouldRenderSkipLink } from './utils/skipLinkRoutes'
import RedirectHandler from './components/RedirectHandler'
import PolicySystemAppShell from './components/PolicySystemAppShell'
import { PolicySystemStatusProvider } from './context/PolicySystemStatusProvider'
import { usePortalAuth } from './auth/authContexts'
import { useApi } from './services/api'
import PostLoginLoadingScreen from './components/PostLoginLoadingScreen'
import RequireAuth from './components/RequireAuth'
import RequireMfa from './components/RequireMfa'
import { isDevMode } from './utils/devMode'
import { clientLogError } from './utils/clientLog'
import { consumePostLogoutLoginIntent } from './utils/postLogoutLogin'
import {
  captureEmailVerificationRequiredFromCallback,
  hasAuth0CallbackErrorInLocation,
} from './utils/auth0EmailVerification'
import { useAuth0 } from '@auth0/auth0-react'
import { isMarketingAuthEntryPath, marketingAuthGatewayPath } from './utils/marketingAuthEntry'
import { consumeAuthReturnTo, isSafeInternalPath } from './auth/authReturnTo'

// Local-only scenario panel — `import.meta.env.DEV` is false in vite build, so this chunk is omitted.
const DevScenarioPanelSlot = import.meta.env.DEV
  ? lazy(() =>
      import('./dev/policyScenarios/DevScenarioPanelHost').then((m) => ({
        default: m.DevScenarioPanelHost,
      }))
    )
  : null

// Component to handle auto-login check
function AutoLoginHandlerCore({ auth0Error }: { auth0Error?: unknown }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = usePortalAuth()
  const api = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const hasBootstrapped = useRef(false)
  const marketingAuthRedirectStarted = useRef(false)

  useEffect(() => {
    marketingAuthRedirectStarted.current = false
  }, [location.pathname, location.search])

  useEffect(() => {
    if (isDevMode()) {
      return
    }

    const checkAutoLogin = async () => {
      const authRoutes = ['/signin', '/register', '/auth/redirect', '/']
      const isAuthRoute = authRoutes.includes(location.pathname)

      if (isAuthRoute) {
        const checkSession = async (attempt = 1) => {
          try {
            if (location.pathname === '/auth/redirect' && !isAuthenticated) {
              if (captureEmailVerificationRequiredFromCallback(auth0Error)) {
                navigate('/signin', { replace: true })
                return
              }
              if (hasAuth0CallbackErrorInLocation()) {
                navigate('/signin', { replace: true })
                return
              }
            }

            if (isLoading) {
              if (attempt < 30) {
                setTimeout(() => checkSession(attempt + 1), 200)
              }
              return
            }

            if (!isAuthenticated && consumePostLogoutLoginIntent()) {
              void Promise.resolve(loginWithRedirect()).catch((error) => {
                clientLogError('Post-logout login redirect failed:', error)
              })
              return
            }

            if (
              !isAuthenticated &&
              !marketingAuthRedirectStarted.current &&
              isMarketingAuthEntryPath(location.pathname, location.search)
            ) {
              marketingAuthRedirectStarted.current = true
              const isSignUp = location.pathname === '/register'
              void Promise.resolve(
                isSignUp ? loginWithRedirect({ signup: true }) : loginWithRedirect()
              ).catch((error) => {
                clientLogError('Marketing auth redirect failed:', error)
                navigate(marketingAuthGatewayPath(location.pathname), { replace: true })
              })
              return
            }

            if (isAuthenticated) {
              // MFA / re-auth returns here with appState.returnTo. Honour it instead of
              // always forcing /dashboard (which skipped the SMS challenge UX loop).
              if (location.pathname === '/auth/redirect') {
                const returnTo = consumeAuthReturnTo()
                if (isSafeInternalPath(returnTo)) {
                  hasBootstrapped.current = true
                  sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
                  navigate(returnTo, { replace: true })
                  return
                }
              }

              if (!hasBootstrapped.current) {
                hasBootstrapped.current = true
                try {
                  await api.getUser()
                  const policiesRes = await api.getPolicies()
                  if (policiesRes?.policies?.length > 0) {
                    sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
                    navigate('/dashboard', { replace: true, state: { bootstrapPolicies: policiesRes } })
                  } else {
                    navigate('/confirm-details', { replace: true })
                  }
                  return
                } catch (error) {
                  clientLogError('Failed to bootstrap user policies:', error)
                  navigate('/dashboard', { replace: true })
                  return
                }
              }
              navigate('/dashboard', { replace: true })
              return
            }

            if (attempt < 30) {
              setTimeout(() => checkSession(attempt + 1), 300)
            } else if (location.pathname === '/auth/redirect') {
              captureEmailVerificationRequiredFromCallback(auth0Error)
              navigate('/signin', { replace: true })
            }
          } catch (e) {
            clientLogError('Error checking session:', e)
          }
        }

        checkSession()
        return
      }

      const skipRoutes = [
        '/confirm-details',
        '/confirm-details/verify',
        '/mfa/mobile-required',
        '/mfa/verify',
        '/check-your-details',
        '/match-found',
      ]
      if (skipRoutes.includes(location.pathname)) {
        return
      }

      try {
        if (isLoading) {
          return
        }

        if (isAuthenticated) {
          const publicRoutes = ['/', '/signin', '/register']
          try {
            await api.getUser()
          } catch {
            return
          }
          if (!hasBootstrapped.current) {
            hasBootstrapped.current = true
            try {
              const policiesRes = await api.getPolicies()
              if (publicRoutes.includes(location.pathname)) {
                if (policiesRes?.policies?.length > 0) {
                  sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
                  navigate('/dashboard', { replace: true, state: { bootstrapPolicies: policiesRes } })
                } else {
                  navigate('/confirm-details', { replace: true })
                }
              }
            } catch (error) {
              clientLogError('Failed to bootstrap user policies:', error)
              if (publicRoutes.includes(location.pathname)) {
                navigate('/dashboard', { replace: true })
              }
            }
          } else if (publicRoutes.includes(location.pathname)) {
            navigate('/dashboard', { replace: true })
          }
        }
      } catch (error) {
        clientLogError('Auto-login check failed:', error)
      }
    }

    const timer = setTimeout(() => {
      checkAutoLogin()
    }, 100)

    return () => clearTimeout(timer)
  }, [isAuthenticated, isLoading, loginWithRedirect, navigate, location.pathname, location.search, api, auth0Error])

  return null
}

function AutoLoginHandlerProduction() {
  const { error } = useAuth0()
  return <AutoLoginHandlerCore auth0Error={error} />
}

function AutoLoginHandler() {
  if (isDevMode()) {
    return <AutoLoginHandlerCore />
  }
  return <AutoLoginHandlerProduction />
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {import.meta.env.DEV && <Route path="/test-entsia" element={<TestEntsia />} />}
        {import.meta.env.DEV && <Route path="/test-google-places" element={<TestGooglePlaces />} />}
        <Route path="*" element={
          <AuthProvider>
            <PolicySystemStatusProvider enabled={!isDevMode()}>
              <AppRoutes />
            </PolicySystemStatusProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  )
}

export function RedirectToUpdateAll() {
  const { policyId } = useParams<{ policyId: string }>()
  return <Navigate to={`/policy/${encodeURIComponent(policyId || '')}/update-all`} replace />
}

export function RedirectToPolicyView() {
  const { policyId } = useParams<{ policyId: string }>()
  return <Navigate to={`/policy/${encodeURIComponent(policyId || '')}`} replace />
}

const AUTH_ROUTES = ['/', '/signin', '/register', '/auth/redirect']

function AppRoutes() {
  const location = useLocation()
  const { isAuthenticated, isLoading } = usePortalAuth()
  const isAuthRoute = AUTH_ROUTES.includes(location.pathname)
  const marketingAuthEntry = isMarketingAuthEntryPath(location.pathname, location.search)
  // Keep full-screen loader on /auth/redirect until AutoLoginHandler navigates away. Otherwise a brief
  // window where isLoading && !isAuthenticated both false mounts RedirectHandler and sends users to /signin.
  // Marketing ?login=1 / ?signup=1 also show the loader until Auth0 redirect starts (no gateway flash).
  const showPostLoginLoading =
    isAuthRoute &&
    (isLoading ||
      isAuthenticated ||
      location.pathname === '/auth/redirect' ||
      (marketingAuthEntry && !isAuthenticated))
  const showSkipLink = shouldRenderSkipLink(location.pathname, {
    postLoginLoading: showPostLoginLoading,
  })

  return (
    <>
      {showSkipLink ? <SkipLink /> : null}
      <DocumentTitle />
      <GtmPageView />
      <InactivityGuard />
      <AutoLoginHandler />
      {DevScenarioPanelSlot ? (
        <Suspense fallback={null}>
          <DevScenarioPanelSlot />
        </Suspense>
      ) : null}
      {showPostLoginLoading ? (
        <PostLoginLoadingScreen />
      ) : (
      <PolicySystemAppShell>
      <Routes>
          <Route path="/" element={<PortalRootEntry />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/redirect" element={<RedirectHandler />} />
          <Route element={<RequireAuth />}>
          <Route path="/confirm-details" element={<ConfirmDetails />} />
          <Route path="/confirm-details/verify" element={<PolicyLinkOtpVerification />} />
          <Route path="/mfa/mobile-required" element={<MfaMobileRequired />} />
          <Route path="/mfa/verify" element={<MfaEnrollmentPrompt />} />
          <Route path="/check-your-details" element={<CheckYourDetails />} />
          <Route path="/match-found" element={<MatchFound />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/help/faq" element={<PortalFAQ />} />
          <Route element={<RequireMfa />}>
          <Route path="/claims" element={<ClaimsLanding />} />
          <Route element={<RequireClaimsNotBlocked />}>
          <Route path="/claims/start" element={<PreClaim />} />
          <Route path="/claims/select-type" element={<ClaimTypeSelection />} />
          <Route path="/claims/call-for-assistance" element={<ClaimCallAssistanceStep />} />
          <Route path="/claims/other/subtype" element={<ClaimOtherSubtypeStep />} />
          <Route path="/claims/other/single-vehicle/driver" element={<ClaimOtherSingleVehicleDriverStep />} />
          <Route path="/claims/other/gst" element={<ClaimOtherGstStep />} />
          <Route
            path="/claims/other/single-vehicle/collision-with"
            element={<ClaimOtherSingleVehicleCollisionWithStep />}
          />
          <Route path="/claims/other/single-vehicle/describe" element={<ClaimOtherSingleVehicleDescribeStep />} />
          <Route path="/claims/other/single-vehicle/review" element={<ClaimOtherSingleVehicleReviewStep />} />
          <Route path="/claims/other/single-vehicle/submitted" element={<ClaimOtherSingleVehicleSubmittedStep />} />
          <Route path="/claims/other/multi-vehicle/driver" element={<ClaimOtherMultiVehicleDriverStep />} />
          <Route path="/claims/other/multi-vehicle/other-party" element={<ClaimOtherMultiVehicleOtherPartyStep />} />
          <Route path="/claims/other/multi-vehicle/describe" element={<ClaimOtherMultiVehicleDescribeStep />} />
          <Route path="/claims/other/multi-vehicle/review" element={<ClaimOtherMultiVehicleReviewStep />} />
          <Route path="/claims/other/multi-vehicle/submitted" element={<ClaimOtherMultiVehicleSubmittedStep />} />
          <Route path="/claims/other/weather/driver" element={<ClaimOtherSingleVehicleDriverStep />} />
          <Route path="/claims/other/weather/describe" element={<ClaimOtherWeatherDescribeStep />} />
          <Route path="/claims/other/weather/review" element={<ClaimOtherWeatherReviewStep />} />
          <Route path="/claims/other/weather/submitted" element={<ClaimOtherWeatherSubmittedStep />} />
          <Route path="/claims/other/fire/driver" element={<ClaimOtherSingleVehicleDriverStep />} />
          <Route path="/claims/other/fire/describe" element={<ClaimOtherFireDescribeStep />} />
          <Route path="/claims/other/fire/review" element={<ClaimOtherFireReviewStep />} />
          <Route path="/claims/other/fire/submitted" element={<ClaimOtherFireSubmittedStep />} />
          <Route path="/claims/other/malicious-damage/describe" element={<ClaimOtherMaliciousDamageDescribeStep />} />
          <Route path="/claims/other/malicious-damage/review" element={<ClaimOtherMaliciousDamageReviewStep />} />
          <Route path="/claims/other/malicious-damage/submitted" element={<ClaimOtherMaliciousDamageSubmittedStep />} />
          <Route path="/claims/other/other-loss/describe" element={<ClaimOtherOtherLossDescribeStep />} />
          <Route path="/claims/other/other-loss/review" element={<ClaimOtherOtherLossReviewStep />} />
          <Route path="/claims/other/other-loss/submitted" element={<ClaimOtherOtherLossSubmittedStep />} />
          <Route path="/claims/other/theft/driver" element={<ClaimOtherSingleVehicleDriverStep />} />
          <Route path="/claims/other/theft/describe" element={<ClaimOtherTheftDescribeStep />} />
          <Route path="/claims/other/theft/review" element={<ClaimOtherTheftReviewStep />} />
          <Route path="/claims/other/theft/submitted" element={<ClaimOtherTheftSubmittedStep />} />
          <Route path="/claims/incident" element={<ClaimIncidentStep />} />
          <Route path="/claims/windscreen/gst" element={<ClaimWindscreenGstStep />} />
          <Route path="/claims/windscreen" element={<ClaimWindscreenStep />} />
          <Route path="/claims/windscreen/detail" element={<ClaimWindscreenDetailStep />} />
          <Route path="/claims/windscreen/repair-option" element={<ClaimWindscreenRepairOptionStep />} />
          <Route path="/claims/windscreen/review" element={<ClaimWindscreenReviewStep />} />
          <Route path="/claims/windscreen/submitted" element={<ClaimWindscreenSubmittedStep />} />
          <Route path="/claims/towing" element={<ClaimTowingQuestion />} />
          <Route path="/claims/details" element={<ClaimDetailsPlaceholder />} />
          </Route>
          <Route path="/policy/:policyId" element={<PolicyView />} />
          <Route path="/documents/:policyId" element={<PolicyDocumentsView />} />
          <Route path="/payment/:policyId" element={<PaymentView />} />
          <Route path="/payment/:policyId/change-method" element={<ChangePaymentMethodView />} />
          <Route path="/policy/:policyId/update-listed-drivers" element={<RedirectToPolicyView />} />
          <Route path="/policy/:policyId/edit-driver/:driverId" element={<RedirectToPolicyView />} />
          <Route path="/update-policy/:policyId" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-all" element={<UpdatePolicyAllFields />} />
          <Route path="/policy/:policyId/update-confirmed" element={<PolicyUpdateConfirmation />} />
          <Route path="/policy/:policyId/update-parking-location" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-modifications-accessories" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-recovery-cover" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-policy-contact" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-registration" element={<RedirectToUpdateAll />} />
          <Route path="/policy/:policyId/update-payment-method" element={<RedirectToUpdateAll />} />
          </Route>
          </Route>
      </Routes>
      </PolicySystemAppShell>
      )}
    </>
  )
}

export default App
